<template>
	<!--
		tbl_header_groups 요소 속성
		{
			key: tbl_header요소가 참조할 key,
			name: Header Group에서 표시할 문자열, 
		}
		tbl_headers 요소 속성 
		{
			key: excel_export/ html_table에서 표시할 data 속성,
			groupKey: excel_export/ html_table에서 Header Group이 존재하는 경우, 해당 header가 참조하는 Header Group의 key,
			name: excel_export/ html_table Header에서 표시할 문자열,
			width: excel_export에서 cell 너비,
			dataType: excel_export에서 cell dataType,
			style: excel_export에서 cell dataType ex> { font: { bold: true, color: { argb: 'FFFF0000' } }, numFmt: '#,##0' },
			editable: excel_export에서 editable이 true이고 data의 id가 다른 data의 parentId와 같지 않은 경우, 해당 cell을 노란색 배경으로 표시함.

			tbl_width: html_table에서 cell 너비 ex>'4%',
			td_class: html_table에서 td에 적용할 css class ex> ['al_right', 'red_bold'],
			display_default: html_table에서 cell에 나타낼 default Text ex> { prop: data속성명 } 혹은 '문자열',
			display_filter: vue filter ex>'commaSeparated',
			th_click_event: th 클릭 시 이벤트 emit ex> $emit(th_click_event)
			click_event: html_table에서 cell 클릭 시 이벤트 emit ex> $emit(click_event)
			title: HTML title 속성에 적용 ex> { prop: '속성명' }
			hidden: 권한 등의 이유로 컬럼 표시 여부가 가변적일 경우 사용 
			row_merge: 행병합 정보 ex> [{ start: idx, end: jIdx }, ... ]
			input_type: Input Type('text', 'checkbox', 'radio')을 값으로 함. Input value로 data요소[key]를 가짐.
			select_options: [{ value: '...', display: '...' }, ... ]을 값으로 함. select value로 data요소[key]를 가짐. 
			button_info: ex > { display: '...', class: 'gray_bt', click_event: '...' }
		}
		data 추가 요소 속성 
		{
				:
			tr_class: html_table에서 tr에 적용할 css class
			id: excel_export에서 editable이 true이고 data의 id가 다른 data의 parentId와 같지 않은 경우, 해당 cell을 노란색 배경으로 표시함.
			cell_style: html_table에서 data의 특정 요소만 css class를 적용하여 표시하고자 하는 경우 ex> { key1: 'class1', key2: ['class2', 'class3'] }
		}
		total 행(data에 추가): header속성과 data속성이 함께 쓰인 형태
		{ 
			is_colspan_record: true
			, log_date_str: prev_date
			, columns: [
				{ colspan: 1, key: 'log_date_str', log_date_str: prev_date, title: { prop: display }, .. }, 
				{ colspan: 5, html_str: '<b> .. </b>', td_class: .. } .. 
			]
			, .. 
		}
		** rowspan(그룹핑)하기 위하여 log_date_str속성을 정의함.(해당 컴포넌트에서 사용하는 속성이 아닌, rowspan을 계산하기 위하여 사용되는 속성) 
	-->
	<table class="list_tbl sticky_list_tbl" v-columns-resizable>
		<colgroup>
			<col v-for="(h, idx) in tbl_headers" :key="idx + '/ col'" :style="{ width: h.tbl_width || undefined }" />
		</colgroup>

		<thead>
			<!-- header_groups -->
			<template v-if="tbl_header_groups && tbl_header_groups.length > 0">
				<tr>
					<th v-for="(g, idx) in tbl_header_groups" :key="idx + '/ group'" :colspan="tbl_headers.filter(h => h.groupKey === g.key && !h.hidden).length">{{ g.name }}</th>
				</tr>
			</template>
			<!-- headers -->
			<tr>
				<template v-for="(h, idx) in tbl_headers">
					<th v-if="!h.hidden" :key="idx + '/ th'">
						<a v-if="h.th_click_event" href="javascript:void(0);" @click="$emit(h.th_click_event, h)">{{ h.name }}{{ sortMark(h.key) }}</a>
						<span v-else>{{ h.name }}</span>
					</th>
				</template>
			</tr>
		</thead>
		<tbody>
			<tr v-for="(d, index) in data" :key="index" :class="d.tr_class">
				<template v-if="d.is_colspan_record">
					<template v-for="(col, colIdx) in d.columns">
						<td v-if="!isMerged(col.key, index)" :key="'col/' + index + '/' + colIdx" :colspan="col.colspan" :rowspan="getRowspanCountByKey(col.key, index)" :class="col.td_class" :title="getTitleText(col.title, col)">
							<span v-if="col.html_str" v-html="col.html_str"></span>
							<span v-else>
								<CellText :header="{ key: col.key, display_default: col.display_default, display_filter: col.display_filter }" :row="col" />
							</span>
						</td>
					</template>
				</template>

				<template v-else v-for="(h, jdx) in tbl_headers">
					<td
						v-if="!h.hidden && !(h.row_merge && h.row_merge.some(rm => rm.start < index && index <= rm.end))"
						:key="jdx + '/' + index"
						:rowspan="getRowspanCount(h.row_merge, index)"
						:class="getTdCssClass(h.td_class, d.cell_style, h.key)"
						:title="getTitleText(h.title, d)"
					>
						<a v-if="h.click_event" href="javascript:void(0);" @click="$emit(h.click_event, d)">
							<CellText :header="h" :row="d" />
						</a>

						<input v-else-if="h.input_type" :type="h.input_type" class="tbl_int" v-model="d[h.key]" />

						<select v-else-if="h.select_options" class="tbl_select" v-model="d[h.key]">
							<option v-for="(opt, optIdx) in h.select_options" :key="jdx + '/' + index + '/opt/' + optIdx" :value="opt.value">{{ opt.display }}</option>
						</select>

						<button v-else-if="h.button_info" type="button" :class="h.button_info.class || 'gray_bt'" @click="$emit(h.button_info.click_event, d)">{{ h.button_info.display }}</button>

						<span v-else>
							<CellText :header="h" :row="d" />
						</span>
					</td>
				</template>
			</tr>
		</tbody>
	</table>
</template>

<script>
import CellText from '@/components/common/commonTable/CellText.vue';
export default {
	props: {
		tbl_header_groups: {
			type: Array,
			default: () => [],
		},
		tbl_headers: {
			type: Array,
			default: () => [],
		},
		data: {
			type: Array,
			default: () => [],
		},
		sort_key: {
			type: String,
			default: null,
		},
		sort_direction: {
			type: String,
			default: null,
		},
	},
	components: {
		CellText,
	},
	methods: {
		getTdCssClass(td_class, cell_style, key) {
			const getCls = c => {
				let cls = null;

				if (Array.isArray(c)) cls = [...c];
				else if (typeof c === 'string') cls = [c];
				else cls = [];

				return cls;
			};

			let res = getCls(td_class);
			if (cell_style && cell_style[key]) res.push(...getCls(cell_style[key]));

			return res;
		},
		getTitleText(title, row) {
			if (!title) return null;

			if (typeof title === 'object') return row[title['prop']];
			else return title;
		},
		getRowspanCount(row_merge, index) {
			if (!row_merge) return 1;
			const rm_idx = row_merge.findIndex(rm => rm.start === index);
			return rm_idx > -1 ? row_merge[rm_idx].end - row_merge[rm_idx].start + 1 : 1;
		},
		getRowspanCountByKey(key, index) {
			if (!key) return 1;

			const h_idx = this.getHeaderIndexByKey(key);
			if (h_idx > -1) return this.getRowspanCount(this.tbl_headers[h_idx].row_merge, index);
			else return 1;
		},
		isMerged(key, index) {
			if (!key) return false;

			const h_idx = this.getHeaderIndexByKey(key);
			if (h_idx > -1 && Array.isArray(this.tbl_headers[h_idx].row_merge)) {
				return this.tbl_headers[h_idx].row_merge.some(rm => rm.start < index && index <= rm.end);
			} else {
				return false;
			}
		},
		getHeaderIndexByKey(key) {
			return this.tbl_headers.findIndex(th => th.key === key);
		},
		sortMark(sort_key) {
			if (!this.sort_key || this.sort_key != sort_key) return null;
			else return this.sort_direction == 'ASC' ? '▲' : '▼';
		},
	},
};
</script>

<style scoped scss="lang">
/* 자식요소 hidden */
.child_dp_none :nth-child(n) {
	display: none;
}
/* 빨간 글씨 */
.red_letter {
	color: red;
}
/* 파란 글씨 */
.blue_letter {
	color: blue;
}
/* 굵은 글씨 */
.bold_letter {
	font-weight: bold;
}
/* 배경색 노랑 */
.bg_yellow {
	background-color: yellow !important;
}
</style>
